import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildQuestionItems } from '../buildQuestionItems';
import { buildTeamNodes } from '../buildTeamNodes';
import { ReportFilters } from '../components/ReportFilters';
import { getAdHocGroupKey, type AdHocGroupConfig } from '../filtering';
import type { NewReport } from '../hooks/useNewReport';
import type { AnswerGroup } from '../types';
import { answerGroupFilterLabels } from './answerGroupFilterLabels';
import { Action, Bar, Composer, Field, Hint, Stack } from './tablePlaygroundStyles';

const emptyDraft = (): AdHocGroupConfig => ({ type: 'byAnswer', teamIds: new Set(), answerSelections: [] });

export function AnswerGroupComposer({
  report,
  onCreated,
}: {
  report: NewReport;
  onCreated: (group: AnswerGroup, existing: boolean) => void;
}) {
  const { t } = useTranslation('newReport');
  const { t: tReport } = useTranslation('Report');
  const [name, setName] = useState('');
  const copyFilters = (): AdHocGroupConfig => ({
    type: 'byAnswer',
    teamIds: new Set(report.filters.teamIds),
    answerSelections: report.filters.answerSelections.map((selection) => ({ ...selection })),
  });
  // ReportFilters owns each open drawer's draft. Confirm commits only to this
  // new-group draft; cancel/reset have the same semantics as in the report header.
  const [draft, setDraft] = useState<AdHocGroupConfig>(copyFilters);
  const questions = useMemo(() => (report.rawData ? buildQuestionItems(report.rawData) : []), [report.rawData]);
  const teams = useMemo(
    () => (report.rawData ? buildTeamNodes(report.rawData, tReport) : []),
    [report.rawData, tReport],
  );
  const rules = answerGroupFilterLabels(draft, report.rawData, t);
  const createGroup = () => {
    if (!name.trim() || !draft.answerSelections.length) return;
    const config = { ...draft, label: name.trim() };
    const existing = report.adHocGroups.get(getAdHocGroupKey(config));
    const group = existing ?? report.addAnswerGroup(config);
    if (!group) return;
    onCreated(group, Boolean(existing));
    setName('');
    setDraft(emptyDraft());
  };
  return (
    <Composer>
      <h3>{t('tableTest.newAnswerGroup')}</h3>
      <Hint>{t('tableTest.groupDraftHint')}</Hint>
      <Field>
        {t('tableTest.groupName')}
        <input
          value={name}
          placeholder={t('tableTest.groupNameExample')}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <ReportFilters
        teams={teams}
        questions={questions}
        selectedTeams={draft.teamIds ?? new Set()}
        selectedAnswers={draft.answerSelections}
        onSelectedTeamsChange={(teamIds) => setDraft((previous) => ({ ...previous, teamIds: new Set(teamIds) }))}
        onSelectedAnswersChange={(answerSelections) =>
          setDraft((previous) => ({
            ...previous,
            answerSelections: answerSelections.map((selection) => ({ ...selection })),
          }))
        }
        compact
      />
      <Bar>
        <Action type="button" onClick={() => setDraft(copyFilters())}>
          {t('tableTest.copyReportFilters')}
        </Action>
        <Action type="button" onClick={() => setDraft(emptyDraft())}>
          {t('tableTest.clearGroupRules')}
        </Action>
      </Bar>
      <Hint>{t('tableTest.groupAnswerLogic')}</Hint>
      <Stack>
        <strong>{t('tableTest.groupRules')}</strong>
        {rules.map((rule, index) => (
          <Hint key={index}>{rule}</Hint>
        ))}
        {!draft.answerSelections.length && <Hint>{t('tableTest.groupNeedsAnswers')}</Hint>}
      </Stack>
      <Action primary type="button" disabled={!name.trim() || !draft.answerSelections.length} onClick={createGroup}>
        {t('tableTest.createAndCompareGroup')}
      </Action>
    </Composer>
  );
}
